import { FlexBox, TextCommon } from '@esg/ui';
import { Row, Col, Card, Spin } from 'antd';
import { useMemo } from 'react';
import {
    SendOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    DollarCircleOutlined,
    CalendarOutlined,
    NotificationOutlined
} from '@ant-design/icons';
import { useHttpQuery } from '@esg/framework';
import { agencyApis } from '@esg/business-agency';
import { ChannelBreakdownCard } from './ChannelBreakdownCard';
import { OperationalMetricCard } from './OperationalMetricCard';
import { BranchSubscriptionCard } from './BranchSubscriptionCard';


interface UsageAnalyticsContainerProps {
    readonly branchId: string;
    readonly startTime: string;
    readonly endTime: string;
}

const getPct = (value: number, total: number) => (total > 0 ? Math.round((value / total) * 100) : 0);

const cardStyle = {
    borderRadius: '12px',
    border: '1px solid #eef2f5',
    boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
    height: '100%'
};

export default function UsageAnalyticsContainer({ branchId, startTime, endTime }: UsageAnalyticsContainerProps) {
    const { data: transactionOverview, isFetching: isTxFetching } = useHttpQuery(
        agencyApis.transaction.getOverviewTransaction,
        { query: { branchId, startTime, endTime } }
    );
    const { data: salesOverview, isFetching: isSalesFetching } = useHttpQuery(
        agencyApis.sale.getOverviewSale,
        { query: { branchId, startTime, endTime } }
    );
    const { data: appointmentOverview, isFetching: isApptFetching } = useHttpQuery(
        agencyApis.appointment.getOverviewAppointment,
        { query: { branchId, startTime, endTime } }
    );
    const { data: campaignOverview, isFetching: isCampaignFetching } = useHttpQuery(
        agencyApis.campaign.getOverviewCampaign,
        { query: { branchId, startTime, endTime } }
    );

    const isLoading = isTxFetching || isSalesFetching || isApptFetching || isCampaignFetching ;

    const trafficVolume = useMemo(() => {
        const mail = { sent: 0, delivered: 0, failed: 0 };
        const sms = { sent: 0, delivered: 0, failed: 0 };
        const whatsapp = { sent: 0, delivered: 0, failed: 0 };

        transactionOverview?.metrics?.forEach((metric) => {
            const type = metric.type.toUpperCase().trim();
            const target =
                type === 'MAIL' || type === 'EMAIL' ? mail :
                type === 'SMS' ? sms :
                type === 'WHATSAPP' ? whatsapp :
                null;

            if (!target) return;
            target.sent += metric.totalSent;
            target.delivered += metric.totalDelivered;
            target.failed += metric.totalFailed;
        });

        const buildChannels = (mailVal: number, smsVal: number, whatsappVal: number, total: number) => [
            { name: 'Mail', value: mailVal, percent: `${getPct(mailVal, total)}` },
            { name: 'SMS', value: smsVal, percent: `${getPct(smsVal, total)}` },
            { name: 'WhatsApp', value: whatsappVal, percent: `${getPct(whatsappVal, total)}` }
        ];

        const totalSent = transactionOverview?.totalSent || 0;
        const totalDelivered = transactionOverview?.totalDelivered || 0;
        const totalFailed = transactionOverview?.totalFailed || 0;

        return {
            sent: { total: totalSent, channels: buildChannels(mail.sent, sms.sent, whatsapp.sent, totalSent) },
            delivered: { total: totalDelivered, channels: buildChannels(mail.delivered, sms.delivered, whatsapp.delivered, totalDelivered) },
            failed: { total: totalFailed, channels: buildChannels(mail.failed, sms.failed, whatsapp.failed, totalFailed) }
        };
    }, [transactionOverview]);

    const operationalStats = useMemo(() => {
        const invPaid = salesOverview?.currentSaleCount || 0;
        const invUnpaid = salesOverview?.unpaidCount || 0;
        const invTotal = invPaid + invUnpaid + (salesOverview?.cancelledCount || 0);

        const apptCurrent = appointmentOverview?.currentAppointmentCount || 0;
        const apptPast = appointmentOverview?.pastAppointmentCount || 0;
        const apptTotal = apptCurrent + apptPast;

        const campOngoing = campaignOverview?.totalOngoing || 0;
        const campIdle = campaignOverview?.totalNotRunning || 0;
        const campTotal = campOngoing + campIdle + (campaignOverview?.totalCompleted || 0);

        return {
            invoices: { total: invTotal, paid: invPaid, unpaid: invUnpaid, paidPct: getPct(invPaid, invTotal) },
            appointments: { total: apptTotal, current: apptCurrent, past: apptPast, currentPct: getPct(apptCurrent, apptTotal) },
            campaigns: { total: campTotal, ongoing: campOngoing, idle: campIdle, ongoingPct: getPct(campOngoing, campTotal) }
        };
    }, [salesOverview, appointmentOverview, campaignOverview]);

    return (
        <FlexBox direction="column" gap={20} width="100%">

            {/* Traffic volume cards */}
            <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                    <Spin spinning={isLoading}>
                        <ChannelBreakdownCard
                            title="Sent Vol."
                            total={trafficVolume.sent.total}
                            channels={trafficVolume.sent.channels}
                            icon={<SendOutlined />}
                            iconBgColor="#f0f5ff"
                            iconColor="#1890ff"
                        />
                    </Spin>
                </Col>
                <Col xs={24} md={8}>
                    <Spin spinning={isLoading}>
                        <ChannelBreakdownCard
                            title="Delivered Vol."
                            total={trafficVolume.delivered.total}
                            channels={trafficVolume.delivered.channels}
                            icon={<CheckCircleOutlined />}
                            iconBgColor="#f6ffed"
                            iconColor="#52c41a"
                        />
                    </Spin>
                </Col>
                <Col xs={24} md={8}>
                    <Spin spinning={isLoading}>
                        <ChannelBreakdownCard
                            title="Failed Vol."
                            total={trafficVolume.failed.total}
                            channels={trafficVolume.failed.channels}
                            icon={<CloseCircleOutlined />}
                            iconBgColor="#fff1f0"
                            iconColor="#ff4d4f"
                        />
                    </Spin>
                </Col>
            </Row>

            {/* Operational metrics */}
            <Card title={<TextCommon fontWeight={600} fontSize={15}>Branch Operational Performance Metrics</TextCommon>} size="small" style={cardStyle}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={8}>
                        <Spin spinning={isLoading}>
                            <OperationalMetricCard
                                percent={operationalStats.invoices.paidPct}
                                color="#52c41a"
                                icon={<DollarCircleOutlined style={{ color: '#52c41a' }} />}
                                label="Billing Revenue"
                                totalLabel={`Total: ${operationalStats.invoices.total} Invoices`}
                                badgeKey1="Paid" badgeVal1={operationalStats.invoices.paid}
                                badgeKey2="Unpaid" badgeVal2={operationalStats.invoices.unpaid}
                                status1="success" status2="warning"
                            />
                        </Spin>
                    </Col>
                    <Col xs={24} md={8}>
                        <Spin spinning={isLoading}>
                            <OperationalMetricCard
                                percent={operationalStats.appointments.currentPct}
                                color="#1890ff"
                                icon={<CalendarOutlined style={{ color: '#1890ff' }} />}
                                label="Appointments Capacity"
                                totalLabel={`Total: ${operationalStats.appointments.total} Bookings`}
                                badgeKey1="Active" badgeVal1={operationalStats.appointments.current}
                                badgeKey2="Past" badgeVal2={operationalStats.appointments.past}
                                status1="processing" status2="default"
                            />
                        </Spin>
                    </Col>
                    <Col xs={24} md={8}>
                        <Spin spinning={isLoading}>
                            <OperationalMetricCard
                                percent={operationalStats.campaigns.ongoingPct}
                                color="#722ed1"
                                icon={<NotificationOutlined style={{ color: '#722ed1' }} />}
                                label="Marketing Outreach"
                                totalLabel={`Total: ${operationalStats.campaigns.total} Campaigns`}
                                badgeKey1="Ongoing" badgeVal1={operationalStats.campaigns.ongoing}
                                badgeKey2="Idle" badgeVal2={operationalStats.campaigns.idle}
                                status1="success" status2="default"
                            />
                        </Spin>
                    </Col>
                </Row>
            </Card>
            <BranchSubscriptionCard 
            branchId={branchId} 
            startTime={startTime}
            endTime={endTime} />

        </FlexBox>
    );
}